import { MouseEvent, useCallback, useMemo, useState } from 'react';

/**
 * Use this hook to simplify the usage of Material UI's <Menu />.
 *
 * @usage
 * ```TypeScript
 * const Component: FC = () => {
 *   const { openMenu, ...menu } = useMaterialMenu();
 *   return (
 *     <>
 *       <Button onClick={openMenu}>Click Me</Button>
 *       <Menu {...menu}>
 *         ...
 *       </Menu>
 *     </>
 *   );
 * };
 * ```
 */
export const useMaterialMenu = (): {
  /** A reference to the underlying Menu node. */
  anchorEl: null | HTMLElement;
  /** If the menu is open or not. */
  open: boolean;
  /** Open the menu. */
  openMenu(event: MouseEvent<HTMLElement>): void;
  /** Close the menu. */
  onClose(): void;
} => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = !!anchorEl;

  const openMenu = useCallback((e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget), []);

  const onClose = useCallback(() => setAnchorEl(null), []);

  return useMemo(
    () => ({
      anchorEl,
      open,
      openMenu,
      onClose,
    }),
    [anchorEl, onClose, open, openMenu]
  );
};
